import { passwordRules } from "./passwordPolicy";

/**
 * The account password's rules, under the box, ticked off as they are met
 * (Phase 12.5), in two rows of two:
 *
 *     At least 12 characters     Upper and lower case
 *     At least 1 number          At least 1 symbol, like ! # - ?
 *
 * Shown from the start, so nobody learns them from a refusal. These are the
 * rules the server enforces (passwordPolicy.ts); the strength meter below them
 * is the pages' own, stricter check.
 *
 * The columns are spread to the box's edges, the first starting at its left
 * and the longest item ending at its right, like the strength bar below;
 * packed to the left they looked left-justified under a full-width box.
 *
 * TWO ROWS ON A SCREEN ABOUT 380px WIDE OR MORE. Measured with Windows' font:
 * the two columns need 298px, and the card leaves 400px on a desktop, 295px on
 * a 375px phone and 280px on a 360px one. On those two phones the last item
 * wraps, and "like ! # - ?" is kept together so it moves down whole rather than
 * leaving "- ?" on a line of its own. Fitting them too would take smaller text
 * or less padding on every page, and a third line there costs nothing.
 *
 * The tick or dot sits in a fixed-width slot: a tick is 4px wider than a dot,
 * and without the slot the columns shift sideways as rules are met.
 */
export default function PasswordRules({ password }: { password: string }) {
  const rules = passwordRules(password);
  return (
    <ul className="mt-1 grid grid-cols-[auto_auto] justify-between gap-x-3 text-xs" aria-label="Password rules">
      {rules.map((rule) => (
        <li key={rule.id} className={rule.met ? "text-success" : "text-fg-hint"}>
          <span aria-hidden="true" className="inline-block w-3">{rule.met ? "✓" : "•"}</span>
          {keepExamplesTogether(rule.label)}
          <span className="sr-only">{rule.met ? " (done)" : " (still needed)"}</span>
        </li>
      ))}
    </ul>
  );
}

/** "At least 1 symbol, like ! # - ?" wraps after the comma, never between the examples. */
function keepExamplesTogether(label: string) {
  const at = label.indexOf(", like ");
  if (at < 0) return label;
  return (
    <>
      {label.slice(0, at + 1)} <span className="whitespace-nowrap">{label.slice(at + 2)}</span>
    </>
  );
}
